/**
 * ColorTeamSchedule — v3.18.8 (Strategy Phase 2)
 *
 * Schedules the federal capture playbook's review milestones:
 *   Pink (storyboard/strategy review),
 *   Red (compliance + win-strategy review),
 *   Gold (final exec / bid validation review),
 *   White (optional pre-Pink internal walk-through).
 *
 * Endpoints:
 *   GET/POST/PATCH/DELETE /api/pursuits/:id/strategy/color-reviews[/:review_id]
 *
 * No email send happens here — `reviewer_emails` is persisted for future
 * notification fan-out. Visual cue: overdue scheduled reviews show in red.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Plus, CalendarDays, Trash2, Check, AlertCircle, Send } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLOR_META = {
  WHITE: { label: 'White', bar: 'bg-slate-300', chip: 'bg-slate-100 text-slate-700', desc: 'Pre-Pink internal walk-through (optional).' },
  PINK: { label: 'Pink', bar: 'bg-pink-400', chip: 'bg-pink-100 text-pink-700', desc: 'Storyboard & win-strategy review.' },
  RED: { label: 'Red', bar: 'bg-red-500', chip: 'bg-red-100 text-red-700', desc: 'Compliance + content + win theme review.' },
  GOLD: { label: 'Gold', bar: 'bg-amber-400', chip: 'bg-amber-100 text-amber-800', desc: 'Executive bid validation — final go.' },
};
const COLOR_ORDER = ['WHITE', 'PINK', 'RED', 'GOLD'];

const STATUS_META = {
  SCHEDULED: { label: 'Scheduled', tone: 'bg-slate-100 text-slate-700' },
  IN_PROGRESS: { label: 'In Progress', tone: 'bg-amber-100 text-amber-700' },
  COMPLETE: { label: 'Complete', tone: 'bg-emerald-100 text-emerald-700' },
  CANCELLED: { label: 'Cancelled', tone: 'bg-rose-100 text-rose-700' },
};

function fmtDate(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString(); } catch { return ''; }
}

function isOverdue(iso, status) {
  if (!iso || status === 'COMPLETE' || status === 'CANCELLED') return false;
  return new Date(iso).getTime() < Date.now();
}

export default function ColorTeamSchedule({ pursuitId }) {
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    color: 'PINK',
    scheduled_date: '',
    reviewer_emails_raw: '',
    notes: '',
  });

  const fetchAll = useCallback(async () => {
    if (!pursuitId) return;
    setLoading(true);
    try {
      // volume_id=none: only pursuit-wide reviews (WHITE/GOLD, or PINK/RED
      // not tied to a volume) — volume-scoped Pink/Red reviews created by
      // Proposal Architect have their own panel and shouldn't show here too.
      const res = await fetch(
        `${API_URL}/api/pursuits/${pursuitId}/strategy/color-reviews?volume_id=none`,
        { headers: getAuthHeaders() },
      );
      if (res.ok) {
        const body = await res.json();
        setReviews(body.reviews || []);
      }
    } finally {
      setLoading(false);
    }
  }, [pursuitId, getAuthHeaders]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const resetForm = () => {
    setForm({ color: 'PINK', scheduled_date: '', reviewer_emails_raw: '', notes: '' });
    setShowForm(false);
  };

  const create = async () => {
    if (!form.scheduled_date) {
      toast.error('Pick a scheduled date');
      return;
    }
    setSaving(true);
    try {
      const reviewer_emails = (form.reviewer_emails_raw || '')
        .split(/[,;\n]/)
        .map((e) => e.trim())
        .filter(Boolean);
      const res = await fetch(
        `${API_URL}/api/pursuits/${pursuitId}/strategy/color-reviews`,
        {
          method: 'POST',
          headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({
            color: form.color,
            scheduled_date: form.scheduled_date,
            reviewer_emails,
            notes: form.notes,
          }),
        },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.detail || `HTTP ${res.status}`);
      }
      toast.success(`${form.color} review scheduled`);
      resetForm();
      fetchAll();
    } catch (e) {
      toast.error(e.message || 'Failed to create review');
    } finally {
      setSaving(false);
    }
  };

  const updateStatus = async (review, status) => {
    try {
      const res = await fetch(
        `${API_URL}/api/pursuits/${pursuitId}/strategy/color-reviews/${review.id}`,
        {
          method: 'PATCH',
          headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ status }),
        },
      );
      if (!res.ok) throw new Error('Failed to update');
      toast.success(`Marked ${COLOR_META[review.color]?.label} as ${STATUS_META[status].label}`);
      fetchAll();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const remove = async (review) => {
    if (!confirm(`Delete this ${COLOR_META[review.color]?.label} review?`)) return;
    try {
      const res = await fetch(
        `${API_URL}/api/pursuits/${pursuitId}/strategy/color-reviews/${review.id}`,
        { method: 'DELETE', headers: getAuthHeaders() },
      );
      if (!res.ok) throw new Error('Failed to delete');
      toast.success('Review removed');
      fetchAll();
    } catch (e) {
      toast.error(e.message);
    }
  };

  // v3.18.14 — Manually trigger the reviewer-reminder fan-out.
  const sendReminder = async (review) => {
    if (!(review.reviewer_emails || []).length) {
      toast.error('Add reviewer emails before sending a reminder.');
      return;
    }
    try {
      const res = await fetch(
        `${API_URL}/api/pursuits/${pursuitId}/strategy/color-reviews/${review.id}/send-reminder`,
        {
          method: 'POST',
          headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ kind: 'manual' }),
        },
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.detail || `HTTP ${res.status}`);
      if (body.skipped) {
        toast.info(`Skipped — ${body.reason || 'sent recently'}`);
      } else if (body.dispatched) {
        toast.success(`Reminder dispatched to ${body.sent}/${body.attempted} reviewers`);
      } else {
        // Send service not configured — the attempt is still logged.
        toast.warning(
          body.reason === 'no_email_service'
            ? 'Email service is not configured for this environment. Send attempt was logged.'
            : 'Reminder was not dispatched. Check email service configuration.'
        );
      }
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <Card data-testid="color-team-schedule">
      <CardContent className="p-0">
        {/* Color legend + add row */}
        <div className="px-4 py-3 border-b border-slate-100 flex flex-wrap items-center gap-2 bg-slate-50/60">
          <span className="text-xs text-slate-600 mr-2">Standard reviews:</span>
          {COLOR_ORDER.map((c) => (
            <span key={c} className={`text-[11px] px-2 py-0.5 rounded ${COLOR_META[c].chip}`}>
              {COLOR_META[c].label}
            </span>
          ))}
          <div className="ml-auto">
            {!showForm ? (
              <Button
                size="sm"
                onClick={() => setShowForm(true)}
                className="h-7 bg-violet-600 hover:bg-violet-700 text-white"
                data-testid="color-review-add-btn"
              >
                <Plus className="w-3.5 h-3.5 mr-1" /> Schedule Review
              </Button>
            ) : null}
          </div>
        </div>

        {showForm ? (
          <div className="p-4 border-b border-slate-100 bg-violet-50/30 space-y-3" data-testid="color-review-form">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="text-xs font-medium text-slate-700">Color</label>
                <select
                  value={form.color}
                  onChange={(e) => setForm({ ...form, color: e.target.value })}
                  className="h-9 mt-1 w-full border border-slate-200 rounded px-2 text-sm bg-white"
                  data-testid="color-review-color"
                >
                  {COLOR_ORDER.map((c) => (
                    <option key={c} value={c}>{COLOR_META[c].label} — {COLOR_META[c].desc}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-700">Scheduled date</label>
                <Input
                  type="date"
                  value={form.scheduled_date}
                  onChange={(e) => setForm({ ...form, scheduled_date: e.target.value })}
                  className="mt-1"
                  data-testid="color-review-date"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-700">Reviewer emails</label>
                <Input
                  value={form.reviewer_emails_raw}
                  onChange={(e) => setForm({ ...form, reviewer_emails_raw: e.target.value })}
                  placeholder="comma or newline separated"
                  className="mt-1"
                  data-testid="color-review-emails"
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700">Notes</label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Focus areas, location, dial-in, etc."
                rows={2}
                className="mt-1"
                data-testid="color-review-notes"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={resetForm} disabled={saving}>Cancel</Button>
              <Button
                onClick={create}
                disabled={saving}
                className="bg-violet-600 hover:bg-violet-700 text-white"
                data-testid="color-review-save-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
                Save Review
              </Button>
            </div>
          </div>
        ) : null}

        {loading ? (
          <div className="py-10 text-center text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin mx-auto" />
          </div>
        ) : reviews.length === 0 ? (
          <div className="py-10 text-center text-slate-500" data-testid="color-review-empty">
            <CalendarDays className="w-8 h-8 mx-auto text-slate-300 mb-2" />
            <p className="text-sm">No reviews scheduled yet. Add a Pink/Red/Gold milestone above.</p>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {reviews.map((r) => {
              const meta = COLOR_META[r.color] || COLOR_META.PINK;
              const statusMeta = STATUS_META[r.status] || STATUS_META.SCHEDULED;
              const overdue = isOverdue(r.scheduled_date, r.status);
              return (
                <li
                  key={r.id}
                  className="flex items-stretch hover:bg-slate-50"
                  data-testid={`color-review-row-${r.id}`}
                >
                  <div className={`${meta.bar} w-1.5`} aria-hidden />
                  <div className="flex-1 p-3 flex flex-wrap items-start gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded ${meta.chip}`}>
                          {meta.label} Team
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded ${statusMeta.tone}`}>{statusMeta.label}</span>
                        {overdue ? (
                          <span className="text-[11px] inline-flex items-center gap-1 text-rose-700 font-semibold">
                            <AlertCircle className="w-3 h-3" /> Overdue
                          </span>
                        ) : null}
                      </div>
                      <div className="mt-1 text-sm text-slate-800">
                        <span className={overdue ? 'text-rose-700 font-semibold' : ''}>
                          {fmtDate(r.scheduled_date)}
                        </span>
                        {r.reviewer_emails?.length ? (
                          <span className="text-xs text-slate-500 ml-2">
                            · {r.reviewer_emails.length} reviewer{r.reviewer_emails.length === 1 ? '' : 's'}
                          </span>
                        ) : null}
                      </div>
                      {r.reviewer_emails?.length ? (
                        <div className="text-xs text-slate-500 mt-0.5 truncate" title={r.reviewer_emails.join(', ')}>
                          {r.reviewer_emails.join(', ')}
                        </div>
                      ) : null}
                      {r.notes ? (
                        <div className="text-xs text-slate-600 mt-1 italic whitespace-pre-wrap">{r.notes}</div>
                      ) : null}
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {r.reviewer_emails?.length > 0 && r.status !== 'COMPLETE' && r.status !== 'CANCELLED' ? (
                        <Button
                          size="sm" variant="ghost"
                          onClick={() => sendReminder(r)}
                          className="h-7 text-xs text-indigo-700 hover:bg-indigo-50"
                          title="Email reviewers a reminder now"
                          data-testid={`color-review-remind-${r.id}`}
                        >
                          <Send className="w-3.5 h-3.5 mr-1" /> Remind
                        </Button>
                      ) : null}
                      {r.status !== 'IN_PROGRESS' && r.status !== 'COMPLETE' && r.status !== 'CANCELLED' ? (
                        <Button
                          size="sm" variant="ghost"
                          onClick={() => updateStatus(r, 'IN_PROGRESS')}
                          className="h-7 text-xs text-amber-700 hover:bg-amber-50"
                          data-testid={`color-review-start-${r.id}`}
                        >
                          Start
                        </Button>
                      ) : null}
                      {r.status !== 'COMPLETE' ? (
                        <Button
                          size="sm" variant="ghost"
                          onClick={() => updateStatus(r, 'COMPLETE')}
                          className="h-7 text-xs text-emerald-700 hover:bg-emerald-50"
                          data-testid={`color-review-complete-${r.id}`}
                        >
                          <Check className="w-3.5 h-3.5 mr-1" /> Done
                        </Button>
                      ) : null}
                      <Button
                        size="sm" variant="ghost"
                        onClick={() => remove(r)}
                        className="h-7 text-slate-500 hover:text-rose-600 hover:bg-rose-50"
                        data-testid={`color-review-delete-${r.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
